import { modules as mockModules } from "@/mocks/vivelo";

/**
 * Qué curso (tabla "lessons"/"course_progress" de Supabase, campo course_id)
 * corresponde a cada módulo de Mi Ruta. Los módulos 1 (quiz de perfil) y 7
 * (masterclass personalizada) no tienen un curso asociado todavía, así que
 * se quedan con su valor del catálogo de muestra.
 */
export const MODULE_COURSE_ID: Record<number, string> = {
  2: "c1",
  3: "c2",
  4: "c3",
  5: "c4",
  6: "c5",
  8: "c6",
};

export type RouteModule = (typeof mockModules)[number] & { status: string; progress: number };

/**
 * Calcula el progreso real de cada módulo de la ruta combinando el catálogo
 * estático (título, ícono, descripción) con el % real de Supabase
 * (course_progress) cuando el módulo tiene un curso asociado. Se usa tanto
 * en Mi Ruta como en Perfil para que ambas pantallas muestren siempre el
 * mismo progreso.
 */
export function computeRouteModules(progressByCourseId: Map<string, number>): RouteModule[] {
  let nextAssigned = false;
  return mockModules.map((m) => {
    const courseId = MODULE_COURSE_ID[m.id];
    // Si el módulo tiene curso asociado, el progreso real manda siempre
    // (0% si todavía no hay ninguna lección completada -- es correcto: aún
    // no lo ha empezado). Si no tiene curso asociado, se queda con el
    // catálogo de muestra.
    const progress = courseId ? progressByCourseId.get(courseId) ?? 0 : m.progress;

    let status: string;
    if (progress >= 100) status = "completed";
    else if (progress > 0) status = "progress";
    else if (!nextAssigned) {
      status = "next";
      nextAssigned = true;
    } else status = "locked";

    return { ...m, progress, status };
  });
}

export function computeRouteStats(modules: RouteModule[]) {
  const completedModules = modules.filter((m) => m.status === "completed").length;
  const routeProgress = Math.round(modules.reduce((sum, m) => sum + m.progress, 0) / modules.length);
  return { completedModules, totalModules: modules.length, routeProgress };
}

/** Promedio de % de progreso entre todos los cursos reales (c1..c6), para la tarjeta "Cursos" de Perfil. */
export function computeCoursesProgressAvg(progressByCourseId: Map<string, number>): number | null {
  const courseIds = Array.from(new Set(Object.values(MODULE_COURSE_ID)));
  if (!courseIds.length) return null;
  const sum = courseIds.reduce((acc, id) => acc + (progressByCourseId.get(id) ?? 0), 0);
  return Math.round(sum / courseIds.length);
}
